import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiActions } from '../shared/apiActions';
import apiCall from '../shared/apiCall';
import VersionFooter from '../components/VersionFooter';

export default function ResetPassword() {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidResetLink'));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError(t('auth.passwordsDoNotMatch'));
      return;
    }

    if (password.length < 6) {
      setError(t('auth.passwordMinLength'));
      return;
    }

    setLoading(true);

    try {
      const response = await apiCall(
        `/api/set?action=${ApiActions.RESET_PASSWORD}`,
        { token, password },
        'POST'
      );

      if (response.success) {
        setSuccess(true);
        setTimeout(() => {
          navigate('/');
        }, 3000);
      } else {
        setError(response.message || t('auth.failedToResetPassword'));
      }
    } catch (err) {
      console.error('Error:', err);
      setError(t('auth.errorOccurred'));
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <>
        <Container className="py-5">
          <Row className="justify-content-center">
            <Col md={6} lg={5}>
              <Card className="shadow">
                <Card.Body className="p-4 text-center">
                  <i className="bi bi-check-circle fs-1 text-success mb-3"></i>
                  <h2 className="mb-3">{t('auth.passwordResetSuccessful')}</h2>
                  <p className="text-muted mb-4">
                    {t('auth.passwordResetSuccessMessage')}
                  </p>
                  <Button variant="primary" onClick={() => navigate('/')}>
                    {t('auth.goToLogin')}
                  </Button>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
        
        <VersionFooter />
      </>
    );
  }

  return (
    <>
      <Container className="py-5">
      <Row className="justify-content-center">
        <Col md={6} lg={5}>
          <Card className="shadow">
            <Card.Body className="p-4">
              <div className="text-center mb-4">
                <i className="bi bi-shield-lock fs-1 text-primary mb-3"></i>
                <h2 className="mb-2">{t('auth.resetPasswordTitle')}</h2>
                <p className="text-muted">
                  {t('auth.resetPasswordDescription')}
                </p>
              </div>

              {error && <Alert variant="danger">{error}</Alert>}

              <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.newPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder={t('auth.enterNewPassword')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading || !token}
                    minLength={6}
                  />
                  <Form.Text className="text-muted">
                    {t('auth.passwordMinLength')}
                  </Form.Text>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.confirmPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    placeholder={t('auth.confirmNewPassword')}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    disabled={loading || !token}
                    minLength={6}
                  />
                </Form.Group>

                <div className="d-grid gap-2 mb-3">
                  <Button 
                    variant="primary" 
                    type="submit" 
                    disabled={loading || !token}
                  >
                    {loading ? (
                      <>
                        <Spinner
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                          className="me-2"
                        />
                        {t('auth.resetting')}
                      </>
                    ) : t('auth.resetPassword')}
                  </Button>
                </div>

                <div className="text-center">
                  <Button 
                    variant="link" 
                    className="text-decoration-none"
                    onClick={() => navigate('/')}
                  >
                    <i className="bi bi-arrow-left me-1"></i>{t('auth.backToLogin')}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      </Container>
      
      <VersionFooter />
    </>
  );
}

